import { useState } from 'react';
import {
  Box,
  Popover,
  IconButton,
  Tooltip,
  TextField,
  Typography,
  Grid,
} from '@mui/material';
import { Palette } from '@mui/icons-material';
import { useTranslation } from '../common/components/LocalizationProvider';

// Paleta de cores predefinidas (baseada em categorias POI)
const PRESET_COLORS = [
  // Cores primárias
  '#3B82F6', // Azul
  '#10B981', // Verde
  '#F59E0B', // Âmbar
  '#EF4444', // Vermelho
  '#8B5CF6', // Roxo
  '#EC4899', // Rosa
  '#06B6D4', // Ciano
  '#F97316', // Laranja
  // Cores secundárias
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#84CC16', // Lima
  '#FBBF24', // Amarelo
  '#A855F7', // Violeta
  '#F43F5E', // Rose
  '#0EA5E9', // Sky
  '#22C55E', // Esmeralda
  // Tons neutros
  '#6B7280', // Cinza
  '#374151', // Cinza escuro
  '#1F2937', // Quase preto
  '#78716C', // Stone
];

const PoiColorPicker = ({
  value = '#3B82F6',
  onChange,
  disabled = false,
  size = 'medium',
}) => {
  const t = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [customColor, setCustomColor] = useState(value);

  const handleClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (color) => {
    onChange(color);
    setCustomColor(color);
    handleClose();
  };

  const handleCustomColorChange = (event) => {
    const color = event.target.value;
    setCustomColor(color);
    if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
      onChange(color);
    }
  };

  const open = Boolean(anchorEl);

  const buttonSize = size === 'small' ? 28 : 36;
  const colorSize = size === 'small' ? 16 : 24;

  return (
    <>
      <Tooltip title={t('poiSelectColor') || 'Selecionar cor'}>
        <IconButton
          onClick={handleClick}
          disabled={disabled}
          sx={{
            width: buttonSize,
            height: buttonSize,
            border: '2px solid',
            borderColor: 'divider',
            p: 0.5,
            '&:hover': {
              borderColor: 'primary.main',
            },
          }}
        >
          <Box
            sx={{
              width: colorSize,
              height: colorSize,
              borderRadius: '50%',
              backgroundColor: value,
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
            }}
          />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {t('poiMarkerColor') || 'Cor do Marcador'}
          </Typography>

          {/* Grade de cores predefinidas */}
          <Grid container spacing={0.5} sx={{ mb: 2 }}>
            {PRESET_COLORS.map((color) => (
              <Grid key={color}>
                <IconButton
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    width: 32,
                    height: 32,
                    p: 0,
                    border: value === color ? '2px solid' : '2px solid transparent',
                    borderColor: value === color ? 'primary.main' : 'transparent',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 0.5,
                      backgroundColor: color,
                      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
                    }}
                  />
                </IconButton>
              </Grid>
            ))}
          </Grid>

          {/* Cor customizada */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Palette fontSize="small" color="action" />
            <TextField
              size="small"
              value={customColor}
              onChange={handleCustomColorChange}
              placeholder="#3B82F6"
              inputProps={{
                maxLength: 7,
                style: { fontFamily: 'monospace' },
              }}
              sx={{ flex: 1 }}
            />
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(customColor) ? customColor : '#ccc',
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default PoiColorPicker;
